import { useState, useEffect } from "react";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { addCategory, getCategory, updateCategory, translateCategoryLanguages } from ".";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AdminPageLayout, AdminSectionCard } from "../../components/admin/AdminPageLayout";
import { AdminAction, AdminFormFooter } from "../../components/admin";
import { resolveMediaUrl } from "../../utils/mediaUrl";

const inputClassName =
  "block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4";

function getApiErrorMessage(error, fallback) {
  return (
    error.response?.data?.message ||
    error.response?.data?.data?.message ||
    error.message ||
    fallback
  );
}

export default function AddCategory() {
  const navigate = useNavigate();
  const location = useLocation();

  const [ID, setId] = useState(null);
  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [hindi, setHindi] = useState("");
  const [gujarati, setGujarati] = useState("");
  const [kannada, setKannada] = useState("");
  const [marathi, setMarathi] = useState("");
  const [odia, setOdia] = useState("");
  const [malayalam, setMalayalam] = useState("");
  const [tamil, setTamil] = useState("");
  const [telgu, setTelugu] = useState("");
  const [bangla, setBangla] = useState("");
  const [punjabi, setPunjabi] = useState("");
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [image, setImage] = useState(
    "/placeholder-image.jpg"
  );

  const canConvertLanguages = Boolean(String(name ?? "").trim()) && !translating;

  const fetchData = async () => {
    try {
      const response = await getCategory(ID);
      const responseData = response.data;
      setData(responseData?.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to load category"), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    }
  };

  useEffect(() => {
    const id = location.pathname.split("/")[2];
    if (!isNaN(id)) {
      setId(id);
    }
  }, []);

  useEffect(() => {
    if (ID !== null) {
      fetchData();
    }
  }, [ID]);

  useEffect(() => {
    if (data !== null) {
      setImage(resolveMediaUrl(data.imageUrl));
      setBangla(data.bangla || "");
      setTamil(data.tamil || "");
      setMalayalam(data.malayalam || "");
      setOdia(data.odia || "");
      setName(data.name || "");
      setTelugu(data.telugu || "");
      setHindi(data.hindi || "");
      setGujarati(data.gujarati || "");
      setKannada(data.kannada || "");
      setMarathi(data.marathi || "");
      setPunjabi(data.punjabi || "");
    }
  }, [data]);

  const handleConvertLanguages = async () => {
    const englishText = String(name ?? "").trim();
    if (!englishText || translating) return;

    try {
      setTranslating(true);
      const response = await translateCategoryLanguages(englishText);
      const translations = response?.data?.data || {};

      if (translations.hindi != null) setHindi(translations.hindi);
      if (translations.gujarati != null) setGujarati(translations.gujarati);
      if (translations.marathi != null) setMarathi(translations.marathi);
      if (translations.kannada != null) setKannada(translations.kannada);
      if (translations.odia != null) setOdia(translations.odia);
      if (translations.malayalam != null) setMalayalam(translations.malayalam);
      if (translations.tamil != null) setTamil(translations.tamil);
      if (translations.telugu != null) setTelugu(translations.telugu);
      if (translations.bangla != null) setBangla(translations.bangla);
      if (translations.punjabi != null) setPunjabi(translations.punjabi);

      toast.success("Translations applied to other languages", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to convert to other languages"), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    } finally {
      setTranslating(false);
    }
  };

  const handleUploadVideo = async () => {
    try {
      const isBlank = (value) => !String(value ?? "").trim();

      // Same required text fields for create and edit (every label marked *).
      const missingRequiredText =
        isBlank(name) ||
        isBlank(hindi) ||
        isBlank(gujarati) ||
        isBlank(marathi) ||
        isBlank(kannada) ||
        isBlank(odia) ||
        isBlank(malayalam) ||
        isBlank(tamil) ||
        isBlank(telgu) ||
        isBlank(bangla) ||
        isBlank(punjabi);

      // Image remains required only on create; edit can keep the existing image.
      if (missingRequiredText || (!ID && !file)) {
        return toast.error("Fill All Fields !", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
          theme: "light",
        });
      }

      setLoading(true);
      const formData = new FormData();
      formData.append("priority", 1);
      formData.append("name", String(name).trim());
      formData.append("hindi", String(hindi).trim());
      formData.append("gujarati", String(gujarati).trim());
      formData.append("marathi", String(marathi).trim());
      formData.append("kannada", String(kannada).trim());
      formData.append("odia", String(odia).trim());
      formData.append("malayalam", String(malayalam).trim());
      formData.append("tamil", String(tamil).trim());
      formData.append("telugu", String(telgu).trim());
      formData.append("bangla", String(bangla).trim());
      formData.append("punjabi", String(punjabi).trim());

      if (!ID) {
        if (!file) {
          setLoading(false);
          return toast.error(" Upload Image  !", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: true,
            theme: "light",
          });
        }
        formData.append("image", file);
        const response = await addCategory(formData);
        if (response) {
          setLoading(false);
          navigate(`/category`, { replace: true, state: { results: true } });
        }
      } else {
        if (file instanceof File) {
          formData.append("image", file);
        }
        const response = await updateCategory(ID, formData);
        if (response) {
          setLoading(false);
          navigate(`/category`, { replace: true, state: { update: true } });
        }
      }
    } catch (error) {
      setLoading(false);
      toast.error(getApiErrorMessage(error, "Error saving category"), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
      console.log(error);
    }
  };

  const changeHandler = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedFormats = ["image/png", "image/webp", "image/jpeg"];
    if (allowedFormats.includes(file.type)) {
      setFile(file);
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImage(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    } else {
      event.target.value = "";
      toast.error(
        "Image type should be image/png', 'image/webp', 'image/jpeg ",
        {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        }
      );
    }
  };

  return (
    <AdminPageLayout
      eyebrow="Content Structure"
      title={ID !== null ? "Edit Category" : "Add Category"}
      description="Upload a category image and maintain translations in one consistent form layout."
      actions={
        <AdminAction
          variant="outline"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => navigate("/category")}
        >
          Back to listing
        </AdminAction>
      }
    >
      <AdminSectionCard title="Category details" description="Update the label, translations, and image for this category.">
        {ID !== null && data?.creator?.fullName && (
          <p className="mb-4 text-sm text-stone-600">
            <span className="font-medium text-stone-700">Created By:</span>{" "}
            {data.creator.fullName}
          </p>
        )}
        <div className="admin-form-body overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-1 gap-6">
            {/* Large Drag & Drop Zone */}
            <div>
              <label className="block text-sm font-medium leading-6 text-stone-700 mb-3">
                Category Image
              </label>
              <div
                className="min-h-[200px] border-2 border-dashed rounded-xl bg-stone-50 flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-stone-100 hover:border-stone-400"
                onClick={() => document.getElementById("category-image-input").click()}
              >
                <input
                  id="category-image-input"
                  type="file"
                  accept="image/png, image/webp, image/jpeg"
                  onChange={changeHandler}
                  className="hidden"
                />
                {image ? (
                  <div className="w-full h-full flex items-center justify-center p-4">
                    <img
                      src={image}
                      alt="Preview"
                      className="max-h-[180px] object-contain rounded-lg"
                      onError={() => setImage("")}
                    />
                  </div>
                ) : (
                  <div className="text-center p-6">
                    <svg className="mx-auto h-12 w-12 text-stone-400 mb-3" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-stone-700 font-medium">Click or drag file to this area to upload</p>
                    <p className="text-stone-400 text-sm mt-1">PNG, WEBP, JPEG (max 5MB)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Form Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={name}
                  placeholder="Enter category name"
                  className={inputClassName}
                  onChange={(e) => setName(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div className="flex items-end">
                <AdminAction
                  variant="outline"
                  onClick={handleConvertLanguages}
                  disabled={!canConvertLanguages}
                  loading={translating}
                  fullWidth
                >
                  Convert to other languages
                </AdminAction>
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Hindi Name *
                </label>
                <input
                  type="text"
                  value={hindi}
                  placeholder="Hindi translation"
                  className={inputClassName}
                  onChange={(e) => setHindi(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Gujarati Name *
                </label>
                <input
                  type="text"
                  value={gujarati}
                  placeholder="Gujarati translation"
                  className={inputClassName}
                  onChange={(e) => setGujarati(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Marathi Name *
                </label>
                <input
                  type="text"
                  value={marathi}
                  placeholder="Marathi translation"
                  className={inputClassName}
                  onChange={(e) => setMarathi(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Kannada Name *
                </label>
                <input
                  type="text"
                  value={kannada}
                  placeholder="Kannada translation"
                  className={inputClassName}
                  onChange={(e) => setKannada(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Odia Name *
                </label>
                <input
                  type="text"
                  value={odia}
                  placeholder="Odia translation"
                  className={inputClassName}
                  onChange={(e) => setOdia(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Malayalam Name *
                </label>
                <input
                  type="text"
                  value={malayalam}
                  placeholder="Malayalam translation"
                  className={inputClassName}
                  onChange={(e) => setMalayalam(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Tamil Name *
                </label>
                <input
                  type="text"
                  value={tamil}
                  placeholder="Tamil translation"
                  className={inputClassName}
                  onChange={(e) => setTamil(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Telugu Name *
                </label>
                <input
                  type="text"
                  value={telgu}
                  placeholder="Telugu translation"
                  className={inputClassName}
                  onChange={(e) => setTelugu(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Bangla Name *
                </label>
                <input
                  type="text"
                  value={bangla}
                  placeholder="Bangla translation"
                  className={inputClassName}
                  onChange={(e) => setBangla(e.target.value)}
                  disabled={translating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                  Punjabi Name *
                </label>
                <input
                  type="text"
                  value={punjabi}
                  placeholder="Punjabi translation"
                  className={inputClassName}
                  onChange={(e) => setPunjabi(e.target.value)}
                  disabled={translating}
                />
              </div>
            </div>
          </div>
        </div>

        <AdminFormFooter>
          <AdminAction onClick={handleUploadVideo} loading={loading} disabled={translating}>
            {ID !== null ? "Update Category" : "Add Category"}
          </AdminAction>
        </AdminFormFooter>
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
